import { useTranslation } from 'react-i18next';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { IconButton, useMediaQuery } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { Period } from 'components/AnalyticsPage/Periods';
import { Steps } from 'components/AnalyticsPage/Spets';
import { VulTypes } from 'components/AnalyticsPage/VulTypes';
import { TrapType } from 'components/AnalyticsPage/TrapType';
import Toggle from 'components/Common/Toggle';
import './ChartTitle.scss';
import { MediaBreakpoints } from 'hooks/Screen/useScreenSize';
import HeatMapYearDropdown from 'pages/DevicesDetail/MCT/MCTDetailCharts/Components/HeatMapYearDropdown';
import HeatMapMonthDropdown from 'pages/DevicesDetail/MCT/MCTDetailCharts/Components/HeatMapMonthDropdown';

interface ChartTitleProps {
  title: string;
  period?: string;
  handlePeriodChange?: (newPeriod: Period) => void;
  startDate?: string;
  setStartDate?: (value: string) => void;
  endDate?: string;
  setEndDate?: (value: string) => void;
  showCustom?: boolean;
  showStepFilter?: boolean;
  step?: string;
  setStep?: (value: string) => void;
  showVulTypeFilter?: boolean;
  vulType?: string;
  setVulType?: (value: string) => void;
  showSetTrapTypeFilter?: boolean;
  trapType?: string;
  setTrapType?: (value: TrapType) => void;
  showSwitchTrapType?: boolean;
  switchTrapType?: TrapType;
  setSwitchTrapType?: (value: TrapType) => void;
  switchTrapTypeLoading?: boolean;
  showSwitchChartType?: boolean;
  isBarChart?: boolean;
  setIsBarChart?: (isBar: boolean) => void;
  currentYear?: string;
  selectedMonth?: string | null;
  setCurrentYear?: (year: string) => void;
  setSelectedMonth?: (month: string | null) => void;
  setChartHeight?: (height: number) => void;
}

const ChartTitle: React.FC<ChartTitleProps> = ({
  title,
  period,
  endDate,
  handlePeriodChange,
  setEndDate,
  setStartDate,
  showCustom,
  startDate,
  showStepFilter = false,
  step = Steps.DAY,
  setStep,
  showVulTypeFilter = false,
  vulType = VulTypes.SANITAR,
  setVulType,
  showSetTrapTypeFilter = false,
  trapType = TrapType.ALL,
  setTrapType,
  showSwitchTrapType = false,
  switchTrapType = TrapType.MCT,
  setSwitchTrapType,
  switchTrapTypeLoading = false,
  showSwitchChartType = false,
  isBarChart,
  setIsBarChart,
  currentYear,
  setCurrentYear,
  selectedMonth,
  setSelectedMonth,
  setChartHeight,
}) => {
  const { t } = useTranslation();
  const mobile = useMediaQuery(MediaBreakpoints.mobile);

  return (
    <div className="d-flex flex-column justify-content-between gap-2">
      <div
        className={`d-flex position-relative ${mobile && (showSetTrapTypeFilter || showSwitchChartType || showSwitchTrapType) ? 'justify-content-start' : 'justify-content-center'}`}
      >
        <div className="d-flex align-items-center">
          <h4 className="text-truncate m-0">{title}</h4>
        </div>
        {!mobile && showSetTrapTypeFilter && (
          <div className="d-flex justify-content-center mx-3">
            <select
              className="form-select rounded-pill ms-2"
              style={{
                height: '23px',
                paddingTop: '0px',
                paddingBottom: '0px',
                fontSize: '12px',
                lineHeight: '21px',
                minHeight: '23px',
              }}
              value={trapType}
              onChange={e => setTrapType!(e.target.value as TrapType)}
            >
              <option value={TrapType.ALL}>{t('ChartsAnalitics.all_traps_captures.SetTrapType.ALL')}</option>
              <option value={TrapType.MCT}>{t('ChartsAnalitics.all_traps_captures.SetTrapType.MCT')}</option>
              <option value={TrapType.SMST}>{t('ChartsAnalitics.all_traps_captures.SetTrapType.SMST')}</option>
            </select>
          </div>
        )}
        {showSwitchChartType && setIsBarChart && (
          <div className="toggle_trap">
            <Toggle
              checked={isBarChart!}
              onChange={() => setIsBarChart(!isBarChart)}
              label={t('ChartsAnalitics.all_traps_activity_chart.bar_chart')}
              leftLabel={t('ChartsAnalitics.all_traps_activity_chart.line_chart')}
              size="sm"
              className="flex align-items-end"
            />
          </div>
        )}
        {showSwitchTrapType && (
          <div className="toggle_trap">
            <Toggle
              checked={switchTrapType === TrapType.SMST}
              onChange={() => {
                if (switchTrapTypeLoading) {
                  enqueueSnackbar(t('ChartsAnalitics.all_traps_activity_chart.data_is_loading'), {
                    variant: 'warning',
                    action: key => (
                      <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                        <CloseIcon />
                      </IconButton>
                    ),
                  });
                } else {
                  setSwitchTrapType!(
                    switchTrapType === TrapType.SMST
                      ? (switchTrapType = TrapType.MCT)
                      : (switchTrapType = TrapType.SMST),
                  );
                }
              }}
              label={t('ChartsAnalitics.all_traps_activity_chart.toggle_label_right')}
              leftLabel={t('ChartsAnalitics.all_traps_activity_chart.toggle_label_left')}
              size="sm"
              className="flex align-items-end"
            />
          </div>
        )}
        {!mobile && showStepFilter && setStep && (
          <div className="d-flex justify-content-center mx-3">
            <select className="form-select rounded-pill ms-2" value={step} onChange={e => setStep(e.target.value)}>
              <option value={Steps.WEEK}>{t('ChartsAnalitics.bait_chart.filter.weekly')}</option>
              <option value={Steps.MONTH}>{t('ChartsAnalitics.bait_chart.filter.monthly')}</option>
              <option value={Steps.DAY}>{t('ChartsAnalitics.bait_chart.filter.daily')}</option>
            </select>
          </div>
        )}
        {showVulTypeFilter && setVulType && (
          <div className="d-flex justify-content-center mx-3">
            <select
              className="form-select rounded-pill ms-2"
              value={vulType}
              onChange={e => setVulType(e.target.value)}
            >
              <option value={VulTypes.PRACTICE}>Practice vulnerabilities</option>
              <option value={VulTypes.SANITAR}>Sanitar vulnerabilities</option>
              <option value={VulTypes.TECH}>Tehnical vulnerabilities</option>
            </select>
          </div>
        )}
      </div>
      {mobile && showSetTrapTypeFilter && (
        <div className="d-flex justify-content-center">
          <select
            className="form-select rounded-pill"
            value={trapType}
            onChange={e => setTrapType!(e.target.value as TrapType)}
          >
            <option value={TrapType.ALL}>{t('ChartsAnalitics.all_traps_captures.SetTrapType.ALL')}</option>
            <option value={TrapType.MCT}>{t('ChartsAnalitics.all_traps_captures.SetTrapType.MCT')}</option>
            <option value={TrapType.SMST}>{t('ChartsAnalitics.all_traps_captures.SetTrapType.SMST')}</option>
          </select>
        </div>
      )}
      {mobile && showStepFilter && setStep && (
        <div className="d-flex justify-content-center">
          <select className="form-select rounded-pill" value={step} onChange={e => setStep(e.target.value)}>
            <option value={Steps.WEEK}>{t('ChartsAnalitics.bait_chart.filter.weekly')}</option>
            <option value={Steps.MONTH}>{t('ChartsAnalitics.bait_chart.filter.monthly')}</option>
            <option value={Steps.DAY}>{t('ChartsAnalitics.bait_chart.filter.daily')}</option>
          </select>
        </div>
      )}
      <div className="d-flex flex-wrap flex-md-nowrap justify-content-center">
        {period && !showCustom && (
          <div className="d-flex align-items-center">
            <div className="m-1">
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={e => setStartDate?.(e.target.value)}
              />
            </div>
            <div className="m-1">
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={e => setEndDate?.(e.target.value)}
              />
            </div>
          </div>
        )}
        {showCustom && (
          <button
            className={`btn waves-effect waves-light ${period === Period.CUSTOM ? 'active-toggle' : ''}`}
            onClick={() => handlePeriodChange?.(Period.CUSTOM)}
          >
            {t('ChartsAnalitics.filters.custom')}
          </button>
        )}
        {period && (
          <button
            className={`btn waves-effect waves-light ${period === Period.YEAR ? 'active-toggle' : ''}`}
            onClick={() => handlePeriodChange?.(Period.YEAR)}
            disabled={period === 'YEAR'}
          >
            {t('ChartsAnalitics.filters.year')}
          </button>
        )}
        {period && (
          <button
            className={`btn waves-effect waves-light ${period === Period.THREE_MONTH ? 'active-toggle' : ''}`}
            onClick={() => handlePeriodChange?.(Period.THREE_MONTH)}
            disabled={period === '3MONTH'}
          >
            {t('ChartsAnalitics.filters.3months')}
          </button>
        )}
        {period && (
          <button
            className={`btn waves-effect waves-light ${period === Period.MONTH ? 'active-toggle' : ''}`}
            onClick={() => handlePeriodChange?.(Period.MONTH)}
            disabled={period === 'MONTH'}
          >
            {t('ChartsAnalitics.filters.month')}
          </button>
        )}
      </div>
      <div className="d-flex align-items-center mb-0 fw-bold gap-2 w-100">
        {currentYear && (
          <HeatMapYearDropdown
            currentYear={currentYear}
            setCurrentYear={(year: string) => {
              setCurrentYear?.(year);
              setSelectedMonth?.(null);
              setChartHeight?.(400);
            }}
            selectedMonth={selectedMonth!}
            setSelectedMonth={(month: string | null) => {
              setSelectedMonth?.(month);
            }}
          />
        )}
        {selectedMonth ? (
          <div className="d-flex gap-2 align-items-center justify-content-start">
            <HeatMapMonthDropdown
              selectedMonth={selectedMonth}
              setSelectedMonth={(month: string | null) => setSelectedMonth?.(month)}
            />
            <ul className="pagination p-0 m-0">
              <button
                className="page-link"
                style={{ cursor: 'pointer', color: '#6A37D4', fontFamily: 'Poppins, sans-serif' }}
                onClick={() => {
                  setSelectedMonth?.(null);

                  setChartHeight?.(500);
                }}
              >
                back to year
              </button>
            </ul>
          </div>
        ) : (
          ''
        )}
      </div>
    </div>
  );
};

export default ChartTitle;
